import React from "react";
import {Box, Typography, IconButton} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import {ChevronLeft, ChevronRight} from "@mui/icons-material";
import CustomCourseCard from "./CourseCard";

export interface ContentCategory {
	id: string;
	tag: string;
	content: any[]; // Changed from ContentItem[] to any[] as ContentItem is removed
}

interface ContentCarouselProps {
	categories: ContentCategory[];
	onItemClick?: (item: any) => void; // Changed from ContentItem to any
	className?: string;
}

const useStyles = makeStyles()((theme) => ({
	carouselContainer: {
		width: "100%",
		marginBottom: "32px"
	},
	categoryHeader: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		marginBottom: "16px",
		padding: "0 16px",
		[theme.breakpoints.down("sm")]: {
			padding: "0 8px"
		}
	},
	categoryTitle: {
		fontSize: "24px",
		fontWeight: 700,
		color: theme.palette.text.primary,
		[theme.breakpoints.down("sm")]: {
			fontSize: "20px"
		}
	},
	navigationButtons: {
		display: "flex",
		gap: "8px"
	},
	navButton: {
		width: 40,
		height: 40,
		borderRadius: "50%",
		backgroundColor: theme.palette.background.paper,
		border: `1px solid ${theme.palette.divider}`,
		"&:hover": {
			backgroundColor: theme.palette.action.hover
		},
		"&:disabled": {
			opacity: 0.5
		}
	},
	carouselWrapper: {
		position: "relative",
		overflow: "hidden"
	},
	carouselContent: {
		display: "flex",
		gap: "16px",
		transition: "transform 0.3s ease",
		padding: "0 16px",
		[theme.breakpoints.down("sm")]: {
			gap: "12px",
			padding: "0 8px"
		}
	},
	cardWrapper: {
		flex: "0 0 auto",
		width: "280px",
		[theme.breakpoints.down("sm")]: {
			width: "240px"
		}
	},
	scrollIndicator: {
		display: "flex",
		justifyContent: "center",
		marginTop: "16px",
		gap: "4px"
	},
	dot: {
		width: "8px",
		height: "8px",
		borderRadius: "50%",
		backgroundColor: theme.palette.divider,
		transition: "background-color 0.3s ease"
	},
	activeDot: {
		backgroundColor: theme.palette.primary.main
	}
}));

export const ContentCarousel: React.FC<ContentCarouselProps> = ({
	categories,
	onItemClick,
	className
}) => {
	const {classes, cx} = useStyles();
	const [currentIndexes, setCurrentIndexes] = React.useState<{ [key: string]: number }>({});

	/**
	 * Navigate to the previous set of cards in the carousel
	 */
	const handlePrevious = (categoryId: string) => {
		setCurrentIndexes((prev) => ({
			...prev,
			[categoryId]: Math.max(0, (prev[categoryId] || 0) - 1)
		}));
	};

	/**
	 * Navigate to the next set of cards in the carousel
	 */
	const handleNext = (categoryId: string) => {
		const category = categories.find((cat) => cat.id === categoryId);
		if (!category) return;

		const maxIndex = Math.max(0, category.content.length - 3); // Show 3 cards at a time
		setCurrentIndexes((prev) => ({
			...prev,
			[categoryId]: Math.min(maxIndex, (prev[categoryId] || 0) + 1)
		}));
	};

	/**
	 * Handle item click event
	 */
	const handleItemClick = (item: any) => { // Changed from ContentItem to any
		if (onItemClick) {
			onItemClick(item);
		}
	};

	/**
	 * Get the currently visible cards for a category
	 */
	const getVisibleCards = (content: any[], categoryId: string) => { // Changed from ContentItem[] to any[]
		const currentIndex = currentIndexes[categoryId] || 0;
		const startIndex = currentIndex;
		const endIndex = Math.min(startIndex + 3, content.length);
		return content.slice(startIndex, endIndex);
	};

	/**
	 * Check if previous navigation is possible
	 */
	const canGoPrevious = (categoryId: string) => {
		return (currentIndexes[categoryId] || 0) > 0;
	};

	/**
	 * Check if next navigation is possible
	 */
	const canGoNext = (categoryId: string, contentLength: number) => {
		const currentIndex = currentIndexes[categoryId] || 0;
		return currentIndex < Math.max(0, contentLength - 3);
	};

	return (
		<Box className={cx(classes.carouselContainer, className)}>
			{categories.map((category) => (
				<Box key={category.id} sx={{marginBottom: "40px"}}>
					<Box className={classes.categoryHeader}>
						<Typography className={classes.categoryTitle}>
							{category.tag}
						</Typography>
						<Box className={classes.navigationButtons}>
							<IconButton
								className={classes.navButton}
								onClick={() => handlePrevious(category.id)}
								disabled={!canGoPrevious(category.id)}
							>
								<ChevronLeft />
							</IconButton>
							<IconButton
								className={classes.navButton}
								onClick={() => handleNext(category.id)}
								disabled={!canGoNext(category.id, category.content.length)}
							>
								<ChevronRight />
							</IconButton>
						</Box>
					</Box>

					<Box className={classes.carouselWrapper}>
						<Box className={classes.carouselContent}>
							{getVisibleCards(category.content, category.id).map((item) => (
								<Box key={item.id} className={classes.cardWrapper}>
									<CustomCourseCard
										title={item.title}
										thumbnail={item.thumbnail}
										assignmentType={item.assignmentType}
										dueDate={item.dueDate}
									/>
								</Box>
							))}
						</Box>
					</Box>

					{category.content.length > 3 && (
						<Box className={classes.scrollIndicator}>
							{Array.from({length: Math.ceil(category.content.length / 3)}).map((_, index) => (
								<Box
									key={index}
									className={cx(
										classes.dot,
										index === (currentIndexes[category.id] || 0) && classes.activeDot
									)}
								/>
							))}
						</Box>
					)}
				</Box>
			))}
		</Box>
	);
};

export default ContentCarousel;